/**
 * Button — Apple HIG action button.
 *
 * Three variants: primary (blue), secondary (gray), danger (red).
 * Two sizes: compact (form sheets), full (alert dialogs).
 * Hover/press states use inline style for precise Apple color control.
 *
 * Maps to SwiftUI: Button("Label") { } .buttonStyle(.borderedProminent)
 */

import { type Ref } from 'react';
import { ds } from '../tokens';

interface ButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'danger' | 'tonal' | 'outline';
  size?: 'pill' | 'compact' | 'full';
  disabled?: boolean;
  className?: string;
  buttonRef?: Ref<HTMLButtonElement>;
  /** Optional counter badge (top-right). Uses variant accent color. */
  badge?: number;
  /** Badge color override (defaults to variant accent). */
  badgeColor?: string;
  /** Show a spinning indicator in the badge position (replaces badge count). */
  badgeLoading?: boolean;
}

const SIZE_CLASSES = {
  pill:    `px-3 py-1 ${ds.type.subFootnote} ${ds.type.weight.semibold} ${ds.radius.full}`,
  compact: `px-4 py-2 ${ds.type.bodyMedium} ${ds.radius.full}`,
  full:    `px-6 py-2.5 ${ds.type.body} ${ds.type.weight.medium} ${ds.radius.full}`,
} as const;

/** Badge accent color per variant */
const BADGE_COLORS: Record<string, string> = {
  primary:   ds.color.button.primary.bg,
  secondary: ds.color.raw.systemGray,
  danger:    ds.color.button.danger.bg,
  tonal:     ds.color.raw.systemBlue,
  outline:   ds.color.raw.systemBlue,
};

export function Button({
  onClick,
  children,
  variant = 'secondary',
  size = 'compact',
  disabled = false,
  className = '',
  buttonRef,
  badge,
  badgeColor,
  badgeLoading = false,
}: ButtonProps) {
  const palette = ds.color.button[variant];
  const bg = disabled ? palette.disabledBg : palette.bg;
  const hasBadge = badge !== undefined && !badgeLoading;
  const isOutline = variant === 'outline';
  const outlinePalette = ds.color.button.outline;
  const borderColor = isOutline
    ? (disabled ? outlinePalette.disabledBorder : outlinePalette.border)
    : undefined;

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap ${SIZE_CLASSES[size]} ${palette.text} ${className}`}
      style={{
        backgroundColor: bg,
        transition: 'background-color 120ms ease-in-out, transform 120ms ease-in-out, color 120ms ease-in-out, border-color 120ms ease-in-out',
        boxShadow: 'none',
        ...(isOutline && { border: `1px solid ${borderColor}` }),
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = palette.hover;
          if (isOutline) {
            e.currentTarget.style.color = 'white';
            e.currentTarget.style.borderColor = palette.hover;
          }
          const b = e.currentTarget.querySelector<HTMLElement>('[data-badge]');
          if (b) b.style.boxShadow = 'inset 0 0 0 20px rgba(0,0,0,0.12)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = bg;
        if (isOutline) {
          e.currentTarget.style.color = '';
          e.currentTarget.style.borderColor = borderColor || '';
        }
        const b = e.currentTarget.querySelector<HTMLElement>('[data-badge]');
        if (b) b.style.boxShadow = 'none';
      }}
      onMouseDown={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = palette.press;
          if (isOutline) {
            e.currentTarget.style.borderColor = palette.press;
          }
          e.currentTarget.style.transform = 'scale(0.97)';
          const b = e.currentTarget.querySelector<HTMLElement>('[data-badge]');
          if (b) b.style.boxShadow = 'inset 0 0 0 20px rgba(0,0,0,0.18)';
        }
      }}
      onMouseUp={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = palette.hover;
          if (isOutline) {
            e.currentTarget.style.borderColor = palette.hover;
          }
          e.currentTarget.style.transform = 'scale(1)';
          const b = e.currentTarget.querySelector<HTMLElement>('[data-badge]');
          if (b) b.style.boxShadow = 'inset 0 0 0 20px rgba(0,0,0,0.12)';
        }
      }}
    >
      {children}
      {hasBadge && (
        <span
          data-badge
          className={`flex items-center justify-center px-1 ${ds.radius.full} text-white ${ds.type.weight.semibold} leading-none ${
            size === 'pill'
              ? 'min-w-[17px] h-[17px] text-[11px]'
              : 'min-w-[22px] h-[22px] text-sm'
          }`}
          style={{ backgroundColor: badgeColor || BADGE_COLORS[variant], transition: 'box-shadow 150ms ease-in-out' }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {badgeLoading && (
        <span
          className={`flex items-center justify-center ${ds.radius.full} ${
            size === 'pill'
              ? 'min-w-[17px] h-[17px]'
              : 'min-w-[22px] h-[22px]'
          }`}
          style={{ backgroundColor: badgeColor || BADGE_COLORS[variant] }}
        >
          <svg
            className="animate-spin"
            width={size === 'pill' ? 11 : 14}
            height={size === 'pill' ? 11 : 14}
            viewBox="0 0 16 16"
            fill="none"
          >
            <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="2" opacity="0.3" />
            <path d="M14 8a6 6 0 01-6 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
      )}
    </button>
  );
}
